import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState
} from 'react';
import { DateTime } from 'luxon';
import { DateRange } from '@mui/x-date-pickers-pro/DateRangePicker';
import { useMeContext } from 'modules/me';
import {
  formatDateFromIsoString,
  getStartOfMonth,
  getEndOFMonth,
  MIN_SUPPORTED_DATE
} from 'utils/date-time';
import { getRangeHonoringWeekStartDay } from 'utils/date-range';
import { useWeekStartDay } from 'modules/common/hooks';
import { Period } from 'graphql/types';
import { NoteInsightsViewOptions } from '../NoteInsightsContextProvider';

export const getCurrentMonthDateRange = (): {
  startDate: string;
  endDate: string;
} => {
  const startDate = getStartOfMonth().toISODate();
  const endDate = getEndOFMonth().toISODate();
  return {
    startDate,
    endDate
  };
};

export const getCurrentWeekDateRange = (
  weekStart: number
): {
  startDate: string;
  endDate: string;
} => {
  const { start, end } = getRangeHonoringWeekStartDay({ weekStart });
  const startDate = start ? start.toISODate() : DateTime.local().toISODate();
  const endDate = end ? end.toISODate() : DateTime.local().toISODate();
  return {
    startDate,
    endDate
  };
};
type useNoteInsightsDateRangeType = {
  timesheetPeriodString: string;
  updateCurrentPeriod: (selectedViewOption: NoteInsightsViewOptions) => void;
  cutsomUpdateCurrentPeriod: (value: DateRange<DateTime | null>) => void;
  updateNext: () => void;
  updatePrevious: () => void;
};

type useNoteInsightsDateRangePropType = {
  currentPeriod: Period | null;
  selectedViewOption: NoteInsightsViewOptions;
  disablePrevious: boolean;
  setCurrentPeriod: (period: Period) => void;
  setSelectedViewOption: (selectedViewOption: NoteInsightsViewOptions) => void;
  setDisablePrevious: Dispatch<SetStateAction<boolean>>;
  setDisableNext: Dispatch<SetStateAction<boolean>>;
};

export const useNoteInsightsDateRange = ({
  currentPeriod,
  selectedViewOption,
  setCurrentPeriod,
  setSelectedViewOption,
  setDisablePrevious,
  disablePrevious,
  setDisableNext
}: useNoteInsightsDateRangePropType): useNoteInsightsDateRangeType => {
  const [prevDiffValue, setPrevDiffValue] = useState(-1);
  const me = useMeContext();
  const weekStart = useWeekStartDay();
  const arrowPeriod = useCallback(() => {
    const startDate = DateTime.fromISO(currentPeriod?.startDate || '');
    const endDate = DateTime.fromISO(currentPeriod?.endDate || '');
    if (selectedViewOption !== NoteInsightsViewOptions.CUSTOM) {
      setSelectedViewOption(NoteInsightsViewOptions.CUSTOM);
    }
    let noOfDays;
    if (disablePrevious) {
      noOfDays = prevDiffValue;
    } else {
      noOfDays = endDate.diff(startDate, 'days').days + 1;
    }
    if (noOfDays === 7) return { type: 'weekly', days: noOfDays };
    else if (
      startDate.toISODate() === startDate.startOf('month').toISODate() &&
      endDate.toISODate() === startDate.endOf('month').toISODate()
    ) {
      return { type: 'monthly', days: noOfDays };
    } else {
      return { type: 'custom', days: noOfDays };
    }
  }, [
    currentPeriod?.endDate,
    currentPeriod?.startDate,
    disablePrevious,
    prevDiffValue,
    selectedViewOption,
    setSelectedViewOption
  ]);

  const updateNext = useCallback(() => {
    const period = arrowPeriod();
    const endDate = DateTime.fromISO(currentPeriod?.endDate || '');
    const todayDate = DateTime.local().toISODate();
    let nextEndDate = currentPeriod?.endDate || '';
    if (selectedViewOption !== NoteInsightsViewOptions.CUSTOM) {
      setSelectedViewOption(NoteInsightsViewOptions.CUSTOM);
    }
    const nextStartDate = endDate.plus({ days: 1 }).toISODate();
    switch (period.type) {
      case 'weekly':
        nextEndDate = endDate.plus({ weeks: 1 }).toISODate();
        break;
      case 'monthly':
        nextEndDate = endDate.plus({ weeks: 1 }).endOf('month').toISODate();
        break;
      case 'custom':
        nextEndDate = endDate.plus({ days: period.days }).toISODate();
    }

    if (nextEndDate >= todayDate) {
      setDisableNext(true);
    }
    setCurrentPeriod({
      startDate: nextStartDate,
      endDate: nextEndDate
    });
  }, [
    arrowPeriod,
    currentPeriod?.endDate,
    selectedViewOption,
    setCurrentPeriod,
    setDisableNext,
    setSelectedViewOption
  ]);

  const updatePrevious = useCallback(() => {
    const period = arrowPeriod();
    const startDate = DateTime.fromISO(currentPeriod?.startDate || '');
    let nextStartDate = currentPeriod?.startDate || '';
    const nextEndDate = startDate.minus({ days: 1 }).toISODate();
    switch (period.type) {
      case 'weekly':
        nextStartDate = startDate.minus({ weeks: 1 }).toISODate();
        break;
      case 'monthly':
        nextStartDate = startDate
          .minus({ days: 1 })
          .startOf('month')
          .toISODate();
        break;
      case 'custom':
        nextStartDate = startDate.minus({ days: period.days }).toISODate();
        break;
    }

    if (nextStartDate <= MIN_SUPPORTED_DATE.toISODate()) {
      setDisablePrevious(true);
      setPrevDiffValue(period.days);
      nextStartDate = MIN_SUPPORTED_DATE.toISODate();
    }
    setCurrentPeriod({
      startDate: nextStartDate,
      endDate: nextEndDate || ''
    });
  }, [
    arrowPeriod,
    currentPeriod,
    setCurrentPeriod,
    setDisablePrevious,
    setPrevDiffValue
  ]);

  useMemo(() => {
    const startDate = DateTime.fromISO(
      currentPeriod?.startDate || ''
    ).toISODate();
    const endDate = DateTime.fromISO(currentPeriod?.endDate || '').toISODate();
    const todayDate = DateTime.local().toISODate();
    const minDate = MIN_SUPPORTED_DATE.toISODate();

    if (startDate <= minDate) {
      setDisablePrevious(true);
    } else {
      setDisablePrevious(false);
    }
    if (endDate >= todayDate) setDisableNext(true);
    else {
      setDisableNext(false);
    }
  }, [
    currentPeriod?.endDate,
    currentPeriod?.startDate,
    setDisableNext,
    setDisablePrevious
  ]);

  const timesheetPeriodString = useMemo(() => {
    const { startDate, endDate } = currentPeriod ?? {};
    const formattedStartDate = startDate
      ? formatDateFromIsoString(startDate, me)
      : '-';
    const formattedEndDate = endDate
      ? formatDateFromIsoString(endDate, me)
      : '-';
    return formattedStartDate + ' - ' + formattedEndDate;
  }, [currentPeriod, me]);

  const updateCurrentPeriod = useCallback(
    (selectedViewOption: NoteInsightsViewOptions) => {
      setSelectedViewOption(selectedViewOption);
      let _startDate: string;
      let _endDate: string;
      const {
        startDate: weekStartDate,
        endDate: weekEndDate
      } = getCurrentWeekDateRange(weekStart);
      const {
        startDate: monthStartDate,
        endDate: monthEndDate
      } = getCurrentMonthDateRange();
      switch (selectedViewOption) {
        case NoteInsightsViewOptions.THIS_MONTH:
          setCurrentPeriod({
            startDate: monthStartDate,
            endDate: monthEndDate
          });
          break;
        case NoteInsightsViewOptions.TODAY:
          _startDate = DateTime.local().toISODate();
          _endDate = DateTime.local().toISODate();
          setCurrentPeriod({ startDate: _startDate, endDate: _endDate });
          break;
        case NoteInsightsViewOptions.THIS_WEEK:
          {
            setCurrentPeriod({
              startDate: weekStartDate,
              endDate: weekEndDate
            });
          }
          break;
        case NoteInsightsViewOptions.LAST_NINETY_DAYS:
          _startDate = MIN_SUPPORTED_DATE.toISODate();
          _endDate = DateTime.local().toISODate();
          setCurrentPeriod({ startDate: _startDate, endDate: _endDate });
          break;
      }
    },

    [setSelectedViewOption, weekStart, setCurrentPeriod]
  );

  const cutsomUpdateCurrentPeriod = useCallback(
    (value: DateRange<DateTime | null>) => {
      setSelectedViewOption(NoteInsightsViewOptions.CUSTOM);
      const _startDate = value[0]
        ? value[0].toISODate()
        : DateTime.local().toISODate();
      const _endDate = value[1]
        ? value[1].toISODate()
        : DateTime.local().toISODate();
      if (DateTime.fromISO(_startDate) > DateTime.fromISO(_endDate)) {
        setCurrentPeriod({ startDate: _endDate, endDate: _startDate });
      } else {
        setCurrentPeriod({ startDate: _startDate, endDate: _endDate });
      }
    },
    [setCurrentPeriod, setSelectedViewOption]
  );

  return {
    timesheetPeriodString,
    updateCurrentPeriod,
    cutsomUpdateCurrentPeriod,
    updateNext,
    updatePrevious
  };
};
